import { useState, useEffect, useRef, useCallback, useMemo, createContext, useContext, ReactNode } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { sendAppEmail } from '@/lib/sendEmail';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  profile: any | null;
  loading: boolean;
  authReady: boolean;
  signUp: (email: string, password: string, handle?: string, captchaToken?: string) => Promise<{ error: any; alreadyRegistered?: boolean; needsConfirmation?: boolean }>;
  signIn: (email: string, password: string, captchaToken?: string) => Promise<{ error: any }>;
  signOut: () => Promise<void>;
  refreshProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [user, setUser] = useState<User | null>(null);
  const sessionRef = useRef<Session | null>(null);
  const [profile, setProfile] = useState<any | null>(null);
  const [loading, setLoading] = useState(true);
  const [authReady, setAuthReady] = useState(false);
  const authReadyRef = useRef(false);
  const profileFetchedForUserRef = useRef<string | null>(null);
  const toastRef = useRef(useToast().toast);
  // Keep toast ref fresh without causing re-renders
  toastRef.current = useToast().toast;

  const fetchProfile = async (userId: string) => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('user_id', userId)
        .single();

      if (error && error.code !== 'PGRST116') {
        console.error('Error fetching profile:', error);
        return null;
      }

      return data;
    } catch (error) {
      console.error('Error fetching profile:', error);
      return null;
    }
  };

  useEffect(() => {
    let isMounted = true;

    const applySession = (nextSession: Session | null, event?: string) => {
      if (!isMounted) return;

      const nextUser = nextSession?.user ?? null;
      const nextUserId = nextUser?.id ?? null;

      sessionRef.current = nextSession;
      setUser((prevUser) => {
        const prevUserId = prevUser?.id ?? null;

        // Never clear user on background token events – only on explicit sign-out
        // or when the userId genuinely changes. This prevents a brief null flash
        // when Supabase fires TOKEN_REFRESHED with a stale/null session on tab focus.
        if (prevUserId && !nextUserId && event !== 'SIGNED_OUT') {
          return prevUser;
        }

        return prevUserId === nextUserId ? prevUser : nextUser;
      });

      if (nextUserId) {
        if (profileFetchedForUserRef.current === nextUserId) return;

        profileFetchedForUserRef.current = nextUserId;
        // Fire-and-forget to avoid deadlocks inside auth state processing.
        setTimeout(async () => {
          if (!isMounted) return;
          const profileData = await fetchProfile(nextUserId);
          if (isMounted) {
            setProfile(profileData);
          }
          // Fire welcome email — idempotency_key ensures it's only ever sent once per user.
          if (event === 'SIGNED_IN' && nextUser?.email) {
            const fullName = (nextUser.user_metadata as Record<string, unknown> | undefined)?.full_name as string | undefined;
            void sendAppEmail({
              templateName: 'welcome',
              recipientEmail: nextUser.email,
              templateData: { name: fullName || profileData?.full_name || profileData?.username },
              idempotencyKey: `welcome-${nextUserId}`,
            });
            // Sync user's local timezone into email_preferences so the daily reminder cron fires at 9am their time.
            try {
              const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
              if (tz) {
                void supabase.from('email_preferences').upsert(
                  { user_id: nextUserId, timezone: tz },
                  { onConflict: 'user_id' }
                );
              }
            } catch { /* ignore */ }
          }
        }, 0);
      } else if (event === 'SIGNED_OUT') {
        profileFetchedForUserRef.current = null;
        setProfile(null);
      }
    };

    const bootstrapAuth = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        applySession(session, 'INITIAL_SESSION');
      } finally {
        if (isMounted) {
          authReadyRef.current = true;
          setAuthReady(true);
          setLoading(false);
        }
      }
    };

    void bootstrapAuth();

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, nextSession) => {
        // Ignore transient hydration events until getSession() completes.
        if (!authReadyRef.current) return;
        if (event === 'PASSWORD_RECOVERY') {
          sessionStorage.setItem('swiftix_password_recovery', '1');
          if (window.location.pathname !== '/reset-password') {
            window.history.replaceState(null, '', '/reset-password');
            window.dispatchEvent(new PopStateEvent('popstate'));
          }
        }
        applySession(nextSession, event);
      }
    );

    return () => {
      isMounted = false;
      subscription.unsubscribe();
    };
  }, []);

  const signUp = useCallback(async (email: string, password: string, handle?: string, captchaToken?: string) => {
    try {
      const redirectUrl = `${window.location.origin}/`;

      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: redirectUrl,
          captchaToken,
          data: {
            username: handle || '',
          }
        }
      });

      if (error) {
        console.error('Sign up error:', error);
        return { error };
      }

      // With email protection on, an already-registered address returns a user
      // with no identities and no email is sent. Surface that instead of
      // pretending a confirmation email is on its way.
      if (data.user && (data.user.identities?.length ?? 0) === 0) {
        return { error: null, alreadyRegistered: true };
      }

      return { error: null, needsConfirmation: !data.session };
    } catch (error) {
      console.error('Sign up error:', error);
      return { error };
    }
  }, []);

  const signIn = useCallback(async (email: string, password: string, captchaToken?: string) => {
    try {
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password,
        options: {
          captchaToken,
        },
      });

      if (error) {
        console.error('Sign in error:', error);
        return { error };
      }

      toastRef.current({
        title: "Welcome back!",
        description: "You've been signed in successfully.",
      });

      return { error: null };
    } catch (error) {
      console.error('Sign in error:', error);
      return { error };
    }
  }, []);

  const signOut = useCallback(async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) {
        console.error('Sign out error:', error);
        toastRef.current({
          title: "Error",
          description: "Failed to sign out. Please try again.",
          variant: "destructive",
        });
      } else {
        toastRef.current({
          title: "Signed out",
          description: "You've been signed out successfully.",
        });
      }
    } catch (error) {
      console.error('Sign out error:', error);
    }
  }, []);

  const refreshProfileStable = useCallback(async () => {
    if (user) {
      const profileData = await fetchProfile(user.id);
      setProfile(profileData);
      profileFetchedForUserRef.current = user.id;
    }
  }, [user]);

  const value = useMemo(() => ({
    user,
    session: sessionRef.current,
    profile,
    loading,
    authReady,
    signUp,
    signIn,
    signOut,
    refreshProfile: refreshProfileStable,
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [user, profile, loading, authReady, signUp, signIn, signOut, refreshProfileStable]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};